// Maps a User-dropdown selection to the unified endpoints' { ownerIds, ownerType } parameters.
//
// Server-side counterpart to resolveUserRowPredicate in effortUserFilter.js, which filtered
// already-fetched rows in the browser. The mapping is the same; the difference is that the
// selection now travels to the query instead of the whole client's rows travelling back.
//
// A person can hold several records — a member with memberships at more than one client, or a
// manager assigned to several clients — so the resolved value is always a list.
// @since NEXT_VERSION
import { ALL_USERS, MANAGER_SELF_OPTION_VALUE, OWNER_TYPE_MANAGER, OWNER_TYPE_MEMBER } from "AppConstants";
import { MANAGER_OPTION_PREFIX, siblingRecordIds } from "utils/managerSubtree";

// No owner constraint — the caller's client/manager scope alone decides what comes back.
const NO_OWNER = {};

/**
 * @param selectedValue the dropdown value: ALL_USERS, MANAGER_SELF_OPTION_VALUE, "MGR-<id>", or a userId
 * @param members       membership records [{ id, userId }] used to resolve a userId to member ids
 * @param selfManagerIds the logged-in manager's own manager-record ids, for the "(Me)" option
 * @param managers      the manager records the options were built from, used to resolve a chosen
 *                      manager to every client assignment that person holds
 * @since NEXT_VERSION
 */
export const resolveOwnerParams = (selectedValue, members = [], selfManagerIds = [], managers = []) => {
  if (selectedValue == null || selectedValue === "" || selectedValue === ALL_USERS) {
    return NO_OWNER;
  }

  if (selectedValue === MANAGER_SELF_OPTION_VALUE) {
    const ids = [...new Set((selfManagerIds || []).filter((id) => id != null))];
    return ids.length > 0 ? { ownerIds: ids, ownerType: OWNER_TYPE_MANAGER } : NO_OWNER;
  }

  // A manager option names one record but stands for the person, so it widens to every
  // assignment they hold — the same way the member branch below widens to every membership.
  if (typeof selectedValue === "string" && selectedValue.startsWith(MANAGER_OPTION_PREFIX)) {
    return {
      ownerIds: siblingRecordIds(managers, selectedValue.slice(MANAGER_OPTION_PREFIX.length)),
      ownerType: OWNER_TYPE_MANAGER,
    };
  }

  // A member selection carries the userId; the query filters on member_id, so resolve every
  // membership that user holds. Falling back to the raw value keeps a direct member-id caller
  // working rather than silently matching nothing.
  //
  // Deduplicated because the caller's member list is often accumulated across clients or
  // managers and can hold the same membership more than once.
  const memberIds = [
    ...new Set(
      (Array.isArray(members) ? members : [])
        .filter((m) => String(m.userId ?? m.id) === String(selectedValue))
        .map((m) => m.id)
    ),
  ];

  return {
    ownerIds: memberIds.length > 0 ? memberIds : [selectedValue],
    ownerType: OWNER_TYPE_MEMBER,
  };
};
